import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItemButton,
  ListItemText,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import SaveIcon from "@mui/icons-material/Save";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import Footer from "examples/Footer";
import { adminClient } from "services/apiClient";
import {
  EMPTY_FORM,
  SUPPORTED_LANGUAGES,
  buildKnowledgeBasePayload,
  formFromEntry,
  formFromUnknown,
  getRequiredLanguages,
  validateKnowledgeBaseForm,
} from "./formModel";

const PAGE_SIZE = 25;
const CATEGORY_OPTIONS = [
  ["necessary", "Zorunlu"],
  ["functionality", "İşlevsel"],
  ["analytics", "Analitik"],
  ["marketing", "Pazarlama"],
];
const RESEARCH_OPTIONS = [
  ["primary-source-reviewed", "Birincil kaynak doğrulandı"],
  ["internal-owner-confirmed", "Site sahibi doğruladı"],
  ["pending-review", "İnceleme bekliyor"],
];

function errorMessage(error, fallback) {
  return error.response?.data?.error || error.response?.data?.message || fallback;
}

function KnowledgeBaseManagement() {
  const [queue, setQueue] = useState([]);
  const [pagination, setPagination] = useState({ page: 1, total: 0, totalPages: 0 });
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [queueLoading, setQueueLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [entryLoading, setEntryLoading] = useState(false);
  const [entryLoadFailed, setEntryLoadFailed] = useState(false);
  const [linkedEntryWasQuarantined, setLinkedEntryWasQuarantined] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const selectionRequestRef = useRef(0);

  const fetchQueue = useCallback(async () => {
    setQueueLoading(true);
    setError("");
    try {
      const response = await adminClient.get("/admin/knowledge-base/unknown", {
        params: { page, pageSize: PAGE_SIZE, search: search || undefined },
      });
      setQueue(response.data?.items || []);
      setPagination(response.data?.pagination || { page, total: 0, totalPages: 0 });
    } catch (requestError) {
      setError(errorMessage(requestError, "İnceleme kuyruğu alınamadı."));
    } finally {
      setQueueLoading(false);
    }
  }, [page, search]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const requiredLanguages = useMemo(() => getRequiredLanguages(selected), [selected]);
  const linkedEntryId = selected?.knowledgeBaseEntryId || null;

  const selectUnknown = async (item) => {
    if (saving) return;
    const requestId = ++selectionRequestRef.current;
    setSelected(item);
    setSuccess("");
    setError("");
    setEntryLoadFailed(false);
    setLinkedEntryWasQuarantined(false);
    if (item.knowledgeBaseConflict) {
      setEntryLoading(false);
      setForm(formFromUnknown(item));
      setError("Bu kimliğin dil satırları birden fazla KB kaydına bağlı. Kayıt düzeltilmeden işlem yapılamaz.");
      return;
    }
    if (!item.knowledgeBaseEntryId) {
      setEntryLoading(false);
      setForm(formFromUnknown(item));
      return;
    }

    setEntryLoading(true);
    setForm(EMPTY_FORM);
    try {
      const response = await adminClient.get(`/admin/knowledge-base/${item.knowledgeBaseEntryId}`);
      if (selectionRequestRef.current !== requestId) return;
      const loadedForm = formFromEntry(response.data);
      setLinkedEntryWasQuarantined(loadedForm.status === "quarantined");
      setForm({ ...loadedForm, status: "active" });
    } catch (requestError) {
      if (selectionRequestRef.current !== requestId) return;
      setError(errorMessage(requestError, "Bağlı KB kaydı alınamadı."));
      setEntryLoadFailed(true);
      setForm(EMPTY_FORM);
    } finally {
      if (selectionRequestRef.current === requestId) setEntryLoading(false);
    }
  };

  const startNewEntry = () => {
    if (saving) return;
    selectionRequestRef.current += 1;
    setSelected(null);
    setForm(EMPTY_FORM);
    setEntryLoading(false);
    setEntryLoadFailed(false);
    setLinkedEntryWasQuarantined(false);
    setError("");
    setSuccess("");
  };

  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
  };

  const updateTranslation = (language, field, value) => {
    setForm((current) => ({
      ...current,
      translations: {
        ...current.translations,
        [language]: { ...current.translations[language], [field]: value },
      },
    }));
  };

  const submitForm = async (event) => {
    event.preventDefault();
    setError("");
    setSuccess("");
    if (selected?.knowledgeBaseConflict) {
      setError("Çelişkili KB bağlantısı çözülmeden kayıt uygulanamaz.");
      return;
    }
    if (selected && linkedEntryId && entryLoadFailed) {
      setError("Bağlı KB kaydı yüklenemedi. Kuyruktaki kaydı yeniden seçip tekrar deneyin.");
      return;
    }
    if (selected && linkedEntryId && !Number.isInteger(form.revision)) {
      setError("KB revision bilgisi alınamadı. Kaydı yenileyip tekrar deneyin.");
      return;
    }

    const validationErrors = validateKnowledgeBaseForm(form, selected ? requiredLanguages : []);
    if (validationErrors.length > 0) {
      setError(validationErrors.join(" "));
      return;
    }

    setSaving(true);
    try {
      if (selected && linkedEntryId) {
        await adminClient.put(`/admin/knowledge-base/${linkedEntryId}/resolve`, {
          cookieDefinitionId: selected.targetDefinitionId,
          expectedRevision: form.revision,
          knowledgeBase: buildKnowledgeBasePayload(form),
        });
        setSuccess("Mevcut KB kaydı güncellendi ve müşteri kataloğuna uygulandı.");
      } else if (selected) {
        await adminClient.post("/admin/knowledge-base/resolve", {
          cookieDefinitionId: selected.targetDefinitionId,
          knowledgeBase: buildKnowledgeBasePayload(form),
        });
        setSuccess("KB kaydı oluşturuldu ve müşteri kataloğu güncellendi.");
      } else {
        await adminClient.post("/admin/knowledge-base", buildKnowledgeBasePayload(form));
        setSuccess("KB kaydı oluşturuldu.");
      }
      setSelected(null);
      setForm(EMPTY_FORM);
      setEntryLoadFailed(false);
      setLinkedEntryWasQuarantined(false);
      await fetchQueue();
    } catch (requestError) {
      setError(errorMessage(requestError, "KB işlemi tamamlanamadı."));
    } finally {
      setSaving(false);
    }
  };

  const searchQueue = (event) => {
    event.preventDefault();
    setPage(1);
    setSearch(searchInput.trim());
  };

  return (
    <DashboardLayout>
      <Stack
        direction={{ xs: "column", md: "row" }}
        alignItems={{ md: "center" }}
        justifyContent="space-between"
        spacing={2}
        mb={3}
      >
        <Box>
          <Typography variant="h4" fontWeight="bold">Çerez Kütüphanesi</Typography>
          <Typography variant="body2" color="text.secondary">
            İnceleme kuyruğunu çözümleyin ve doğrulanmış kayıtları müşteri kataloglarına uygulayın.
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={startNewEntry}
          disabled={saving}
          sx={{ color: "#fff" }}
        >
          Yeni KB Kaydı
        </Button>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

      <Grid container spacing={3} alignItems="flex-start">
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Stack spacing={2}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Box>
                    <Typography variant="h6">İnceleme Kuyruğu</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {pagination.total} çözümlenmemiş kimlik
                    </Typography>
                  </Box>
                  <Tooltip title="Kuyruğu yenile">
                    <span>
                      <IconButton onClick={fetchQueue} disabled={queueLoading} aria-label="Kuyruğu yenile">
                        <RefreshIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Stack>

                <Box component="form" onSubmit={searchQueue}>
                  <TextField
                    fullWidth
                    size="small"
                    label="Çerez, domain veya provider ara"
                    value={searchInput}
                    onChange={(event) => setSearchInput(event.target.value)}
                    InputProps={{
                      endAdornment: (
                        <IconButton type="submit" size="small" aria-label="Ara"><SearchIcon /></IconButton>
                      ),
                    }}
                  />
                </Box>

                <Box sx={{ minHeight: 420, maxHeight: 620, overflowY: "auto", mx: -1 }}>
                  {queueLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360 }}>
                      <CircularProgress size={30} />
                    </Stack>
                  ) : queue.length === 0 ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 360, px: 2 }}>
                      <Typography variant="body2" color="text.secondary">Kuyrukta kayıt bulunmuyor.</Typography>
                    </Stack>
                  ) : (
                    <List disablePadding>
                      {queue.map((item) => (
                        <ListItemButton
                          key={`${item.tenantId}-${item.siteId}-${item.name}-${item.observedDomain}`}
                          selected={selected?.targetDefinitionId === item.targetDefinitionId}
                          onClick={() => selectUnknown(item)}
                          disabled={saving}
                          sx={{ borderRadius: 1, mb: 0.5, alignItems: "flex-start" }}
                        >
                          <ListItemText
                            primary={
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Typography variant="body2" fontWeight="bold" sx={{ overflowWrap: "anywhere" }}>
                                  {item.name}
                                </Typography>
                                {item.knowledgeBaseConflict && (
                                  <Chip size="small" color="error" icon={<WarningAmberIcon />} label="Çelişki" />
                                )}
                                {!item.knowledgeBaseConflict && item.knowledgeBaseEntryId && (
                                  <Chip size="small" color="info" label={`KB #${item.knowledgeBaseEntryId}`} />
                                )}
                              </Stack>
                            }
                            secondary={
                              <Box component="span" sx={{ display: "block", mt: 0.5 }}>
                                <Typography component="span" variant="caption" display="block">
                                  {item.siteDomain} · {item.observedDomain || "domain yok"}
                                </Typography>
                                <Typography component="span" variant="caption" color="text.secondary">
                                  Tenant {item.tenantId} / Site {item.siteId} · {getRequiredLanguages(item).join(", ").toUpperCase()}
                                </Typography>
                              </Box>
                            }
                          />
                        </ListItemButton>
                      ))}
                    </List>
                  )}
                </Box>

                {pagination.totalPages > 1 && (
                  <Pagination
                    count={pagination.totalPages}
                    page={page}
                    onChange={(_event, value) => setPage(value)}
                    size="small"
                    color="primary"
                    sx={{ alignSelf: "center" }}
                  />
                )}
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Box component="form" onSubmit={submitForm}>
                <Stack spacing={3}>
                  <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" spacing={1}>
                    <Box>
                      <Typography variant="h6">
                        {selected ? "Kuyruk Kaydını Çözümle" : "Yeni Bilgi Tabanı Kaydı"}
                      </Typography>
                      {selected && (
                        <Typography variant="caption" color="text.secondary">
                          {selected.siteDomain} · gözlenen domain: {selected.observedDomain || "yok"}
                        </Typography>
                      )}
                    </Box>
                    {linkedEntryId && <Chip color="info" label={`Mevcut KB #${linkedEntryId}`} />}
                  </Stack>

                  {linkedEntryWasQuarantined && (
                    <Alert severity="warning">
                      Bu KB kaydı karantinadaydı. Güncelle ve uygula işlemi kaydı aktif hale getirecek.
                    </Alert>
                  )}

                  {entryLoading ? (
                    <Stack alignItems="center" justifyContent="center" sx={{ minHeight: 420 }}>
                      <CircularProgress />
                    </Stack>
                  ) : (
                    <>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={8}>
                          <TextField
                            fullWidth size="small" label="Çerez adı" value={form.name}
                            onChange={(event) => updateField("name", event.target.value)}
                            disabled={Boolean(linkedEntryId)} required
                          />
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <ToggleButtonGroup
                            exclusive fullWidth size="small" value={form.match}
                            onChange={(_event, value) => value && updateField("match", value)}
                            disabled={Boolean(linkedEntryId)}
                          >
                            <ToggleButton value="exact">Tam ad</ToggleButton>
                            <ToggleButton value="prefix">Prefix</ToggleButton>
                          </ToggleButtonGroup>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Kategori</InputLabel>
                            <Select
                              value={form.category} label="Kategori"
                              onChange={(event) => updateField("category", event.target.value)}
                            >
                              {CATEGORY_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="Provider" value={form.provider}
                            onChange={(event) => updateField("provider", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" label="KB domain / sağlayıcı kapsamı" value={form.domain}
                            onChange={(event) => updateField("domain", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            fullWidth size="small" type="url" label="Birincil kaynak / gizlilik URL'si"
                            value={form.privacyLink} onChange={(event) => updateField("privacyLink", event.target.value)}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small">
                            <InputLabel>KB durumu</InputLabel>
                            <Select
                              value={form.status} label="KB durumu"
                              disabled={Boolean(selected)}
                              onChange={(event) => updateField("status", event.target.value)}
                            >
                              <MenuItem value="active">Aktif</MenuItem>
                              <MenuItem value="quarantined">Karantina</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <FormControl fullWidth size="small" required>
                            <InputLabel>Araştırma durumu</InputLabel>
                            <Select
                              value={form.researchStatus} label="Araştırma durumu"
                              onChange={(event) => updateField("researchStatus", event.target.value)}
                            >
                              {RESEARCH_OPTIONS.map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                          <TextField
                            fullWidth multiline minRows={2} size="small" label="İnceleme notu"
                            value={form.reviewReason} onChange={(event) => updateField("reviewReason", event.target.value)}
                          />
                        </Grid>
                      </Grid>

                      <Box>
                        <Typography variant="subtitle2" mb={1.5}>Yerelleştirilmiş içerik</Typography>
                        <Grid container spacing={2}>
                          {SUPPORTED_LANGUAGES.map((language) => {
                            const required = requiredLanguages.includes(language);
                            return (
                              <Grid item xs={12} md={6} key={language}>
                                <Paper variant="outlined" sx={{ p: 2, height: "100%", borderRadius: 1 }}>
                                  <Stack spacing={2}>
                                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                                      <Typography variant="subtitle2">{language.toUpperCase()}</Typography>
                                      {selected && <Chip size="small" label={required ? "Müşteri dili" : "İsteğe bağlı"} />}
                                    </Stack>
                                    <TextField
                                      fullWidth multiline minRows={4} size="small" label="Açıklama"
                                      value={form.translations[language].description}
                                      onChange={(event) => updateTranslation(language, "description", event.target.value)}
                                      required={required}
                                    />
                                    <TextField
                                      fullWidth size="small" label="Süre"
                                      value={form.translations[language].duration}
                                      onChange={(event) => updateTranslation(language, "duration", event.target.value)}
                                      required={required}
                                    />
                                  </Stack>
                                </Paper>
                              </Grid>
                            );
                          })}
                        </Grid>
                      </Box>
                    </>
                  )}

                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      type="submit" variant="contained" startIcon={<SaveIcon />}
                      disabled={saving || entryLoading || entryLoadFailed || selected?.knowledgeBaseConflict}
                      sx={{ color: "#fff", minWidth: 190 }}
                    >
                      {saving ? "Kaydediliyor..." : linkedEntryId ? "Güncelle ve Uygula" : selected ? "Oluştur ve Uygula" : "KB Kaydını Oluştur"}
                    </Button>
                  </Stack>
                </Stack>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
      <Footer />
    </DashboardLayout>
  );
}

export default KnowledgeBaseManagement;
